import { CSS, Dropdown, DropdownButtonProps as NextDropdownButtonProps } from '@nextui-org/react';
import styled from 'styled-components';

import { SearchIcon } from '../../assets';
import AppSearchInput from '../../components/Input/SearchInput';
import ZIndexLayers from '../../services/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import Avatar, { AvatarVariant } from '../Avatar';
import { BOTTOMSHEET_MAX_TOP_MARGIN } from '../BottomSheet/BottomSheet';
import Text from '../Text';

interface ContainerProps {
  $fullWidth?: boolean;
}
export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  ${(props) => props.$fullWidth && 'width: 100%'};
`;

interface TitleProps {
  $alwaysShowTitle?: boolean;
  $color?: string;
}
export const Title = styled.div<TitleProps>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xxs};
  min-height: ${(props: ThemeProp & TitleProps) => (props.$alwaysShowTitle ? props.theme.lineHeight.xs : 0)};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  color: ${(props: ThemeProp & TitleProps) => props.$color || props.theme.colors.greyscale.c050};
`;

export const StyledDropdown = styled(Dropdown).attrs((props) => ({
  ...props,
  isBordered: true,
  offset: 8,
}))``;

interface DropdownButtonProps extends NextDropdownButtonProps, ThemeProp {
  $maxHeight: string;
  $buttonSidesPadding?: number;
  $expandWithoutBorder?: boolean;
  $noPaddingLeft?: boolean;
  $fontSize: string;
  $fontWeight: string;
  $buttonWidth: number;
  $buttonCSS?: any;
}

export const DropDownButton = styled(Dropdown.Button).attrs((props: DropdownButtonProps) => {
  const isDisabled = props.disabled;

  return {
    css: {
      color: props.theme.colors.black,
      height: 'calc($15 - 2px)',
      maxHeight: props.$maxHeight ? props.$maxHeight : 'unset',
      'border-radius': '$xs',
      'border-color': props.theme.colors.neutrals.c200,
      'padding-left': `$${props.$noPaddingLeft ? 0 : props.$buttonSidesPadding ?? 4}`,
      'padding-right': `$${props.$buttonSidesPadding ?? 4}`,
      fontSize: props.$fontSize || '$input',
      fontWeight: props.$fontWeight || '$normal',
      '&.nextui-dropdown-button': {
        justifyContent: 'space-between',
        background: isDisabled ? props.theme.colors.neutrals.c100 : props.theme.colors.layout.surface.default,
        color: isDisabled ? props.theme.colors.content.disabled : props.theme.colors.content.default,
        border: `1px solid ${props.theme.colors.neutrals.c300}`,
      },
      '&.nextui-dropdown-button[aria-haspopup="true"].nextui-dropdown-button[aria-expanded="true"]': {
        transform: 'unset',
        border: props.$expandWithoutBorder ? 'unset' : `1px solid ${props.theme.colors.neutrals.c500}`,
        opacity: 1,
      },
      '.nextui-button-text': {
        color: props.theme.colors.content.default,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        lineHeight: '24px',
        zIndex: ZIndexLayers.default,
      },
      '.nextui-button-icon-right': {
        display: props.iconRight ? 'flex' : 'none',
        stroke: props.theme.colors.neutrals.c500,
        zIndex: ZIndexLayers.default,
      },
      '&.nextui-button--disabled': {
        border: `1px solid ${props.theme.colors.neutrals.c300} !important`,
        background: `${props.theme.colors.neutrals.c100} !important`,
        '.nextui-button-text': {
          color: `${props.theme.colors.content.disabled} !important`,
        },
        svg: {
          stroke: props.theme.colors.neutrals.c500,
        },
      },
      minWidth: `${props.$buttonWidth}px`,
      ...props.$buttonCSS,
    },
    ...props,
  };
})<any>`
  ${(props) =>
    props.$isOpen &&
    !props.$disableSvgAnimation &&
    `svg {
       transform: rotateZ(-180deg);
       transition: all 0.1s ease-in;
    }`}
`;

export const DropDownButtonContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

interface DropDownMenuProps {
  width: number;
  $maxHeight?: string;
}

export const DropDownMenu = styled(Dropdown.Menu).attrs((props) => {
  return {
    ...props,
    containerCss: {
      background: props.theme.colors.background,
      border: `1px solid ${props.theme.colors.greyscale.c800} !important`,
      'border-radius': '0.5rem !important',
      'box-shadow': props.menuContainerCSS?.boxShadow || props.theme.shadows.menu,
      overflowY: 'hidden',
      ...props.menuContainerCSS,
    },
  };
})<any>`
  padding: 0.5rem;
  background: ${(props) => props.theme.colors.background};
  --nextui--dropdownMenuMinWidth: ${(props: DropDownMenuProps) => props.width}px;
  overflow-y: auto;
  max-height: ${(props: DropDownMenuProps) => props.$maxHeight || '20rem'} !important;
  & .nextui-dropdown-item {
    height: unset;
    padding: 0.625rem 0.5rem;

    &.is-selected {
      color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
      background: ${(props: ThemeProp) => props.theme.colors.main.c050};
      border-radius: 0.375rem;
    }

    &.is-disabled {
      .nextui-dropdown-item-content {
        span {
          color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
        }
      }
    }

    &:hover,
    &:focus,
    &:active {
      background: ${(props: ThemeProp) => props.theme.colors.main.c050} !important;
    }
  }
  .nextui-dropdown-item-content {
    overflow: hidden;
    font-size: 2rem;
  }
  .nextui-checkmark {
    display: none;
  }
`;

export const DropDownMenuItem = styled(Dropdown.Item).attrs((props) => {
  return {
    ...props,
    css: {
      marginBottom: '1rem !important',
    },
  };
})``;

export const DropDownItemContainer = styled.div<any>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.25rem;
`;

interface DropDownItemTextProps {
  css?: CSS;
  $isSelected?: boolean;
}

export const DropDownItemText = styled.div<DropDownItemTextProps>`
  display: flex;
  width: 100%;
  font-size: ${(props: ThemeProp & DropDownItemTextProps) =>
    props.css?.fontSize || props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp & DropDownItemTextProps) => props.css?.lineHeight || props.theme.lineHeight.ml};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${(props: ThemeProp & DropDownItemTextProps) =>
    props.$isSelected ? props.theme.colors.main.c600 : props.theme.colors.neutrals.c800};
  margin-left: 1px;
`;

export const DropDownItemDescription = styled(Text)`
  height: 1.137rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp & DropDownItemTextProps) => props.theme.colors.content.subdued.high};
  margin-left: auto;
  width: fit-content;
  flex-shrink: 0;
`;

export const BottomSheetItemText = styled(DropDownItemText)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const BottomSheetItemSecondaryText = styled(Text)`
  margin-left: 0.375rem;
  margin-bottom: 0.25rem;
  height: 1.137rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp & DropDownItemTextProps) =>
    props.$isSelected ? props.theme.colors.main.c300 : props.theme.colors.content.subdued.high};
  flex-shrink: 0;
`;

const BOTTOMSHEET_HEADER_HEIGHT = '4.75rem';
const BOTTOMSHEET_SEARCHBAR_HEIGHT = '3.5rem';

interface BottomSheetContainerProps {
  $isSearchEnabled?: boolean;
  $disablePadding?: boolean;
}

export const BottomSheetContainer = styled.div<BottomSheetContainerProps>`
  /* Fix height in case of search enabled to maintain the height of bottomsheet while searching */
  ${(props: BottomSheetContainerProps) =>
    props.$isSearchEnabled
      ? `height: calc(100dvh - ${BOTTOMSHEET_HEADER_HEIGHT} - ${BOTTOMSHEET_SEARCHBAR_HEIGHT} - ${BOTTOMSHEET_MAX_TOP_MARGIN});`
      : `max-height: calc(100dvh - ${BOTTOMSHEET_HEADER_HEIGHT} - ${BOTTOMSHEET_MAX_TOP_MARGIN});`}
  padding: ${(props: BottomSheetContainerProps) => (props.$disablePadding ? '0 0 1.5rem 0' : '0 1.5rem 2rem 1.5rem')};
  .nextui-radio-container {
    flex-direction: row-reverse;
    width: 100%;
    justify-content: space-between;
    height: 2.75rem;
    padding: 0.75rem;
    gap: 0.5rem;
  }
  .nextui-radio-group-items {
    gap: 0.5rem;
  }

  .nextui-radio--checked {
    .nextui-radio-container {
      background-color: ${(props: ThemeProp) => props.theme.colors.main.c050};
      border-radius: 0.5rem;
    }
  }
  .nextui-radio-label {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    color: ${(props: ThemeProp) => props.theme.colors.black};
    &:not(:first-child) {
      margin-top: 0;
    }
  }
  .nextui-radio-text {
    overflow: hidden;
  }
  .nextui-radio-point {
    flex-shrink: 0;
  }
`;

const commonMobileFontProps = (theme: ThemeProp['theme']) => {
  return `
    font-size: ${theme.fontVariants.desktop.body1.fontSize};
    line-height: ${theme.fontVariants.desktop.body1.lineHeight};
  `;
};

interface SearchInputContainerProps {
  $disablePadding?: boolean;
}

export const SearchInputContainer = styled.div<SearchInputContainerProps>`
  padding: ${(props: SearchInputContainerProps) => (props.$disablePadding ? '0' : '0 1.5rem')};
  margin-bottom: 0.625rem;
`;

export const StyledSearchInput = styled(AppSearchInput)`
  width: 100%;

  & input::placeholder {
    color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 2.5rem !important;
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c800};
    margin-bottom: 1rem;

    ${({ theme }: ThemeProp) => commonMobileFontProps(theme)}
    & input::placeholder {
      color: ${(props: ThemeProp) => props.theme.colors.neutrals.c800};
      ${({ theme }: ThemeProp) => commonMobileFontProps(theme)}
    }
  }
`;

export const StyledSearchIcon = styled(SearchIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  transform: scale(1.4);
`;

export const StyledAvatar = styled(Avatar).attrs(() => ({
  variant: AvatarVariant.SMALL,
}))``;

export const DropdownError = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  color: ${(props: ThemeProp) => props.theme.colors.red};
  margin: 0.125rem 0.5rem 0 0.5rem;
`;
